import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import { RecentChatsList } from "./WorkspaceHome";
import { recentV2Conversations, countProjectChats } from "./ChatListRail";
import { useRunningConversations } from "../../llm/runningConversations";
import NewProjectWizard from "./NewProjectWizard";
import { SkeletonCardGrid, SkeletonRows } from "../../components/workspace/Skeleton";
import EmptyState from "../../components/workspace/EmptyState";

const formatRelative = (iso) => {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  const delta = Date.now() - ts;
  const min = Math.floor(delta / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 14) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
};

export const ProjectCard = ({ programId, project, onRename, onEditHypothesis, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(project.name || "");
  const [hyp, setHyp] = useState(project.hypothesis || "");

  useEffect(() => { setName(project.name || ""); setHyp(project.hypothesis || ""); }, [project.name, project.hypothesis]);

  const startEdit = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setEditing(true);
  };
  const cancelEdit = () => {
    setName(project.name || "");
    setHyp(project.hypothesis || "");
    setEditing(false);
  };
  const saveEdit = async () => {
    const nextName = (name || "").trim();
    const nameChanged = nextName && nextName !== project.name;
    const hypChanged = hyp !== (project.hypothesis || "");
    if (nameChanged) await onRename?.(project.id, nextName);
    if (hypChanged) await onEditHypothesis?.(project.id, hyp);
    setEditing(false);
  };

  if (editing) {
    return (
      <article
        className="home-card home-card--project home-card--edit-mode stone-card"
        data-testid={`v2-project-card-${project.id}`}
        onKeyDown={(e) => {
          if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); }
        }}
      >
        <input
          autoFocus
          className="home-card__title home-card__title-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Project name"
          data-testid={`v2-project-card-name-input-${project.id}`}
        />
        <textarea
          className="home-card__desc home-card__desc-input"
          value={hyp}
          onChange={(e) => setHyp(e.target.value)}
          rows={2}
          placeholder="Add a hypothesis…"
          data-testid={`v2-project-card-hyp-input-${project.id}`}
        />
        <div className="home-card__meta">
          <span className="home-card__chip home-card__chip--project">Project</span>
          <span className="home-card__count">{project.chatCount} chats</span>
          <span className="home-card__count">{project.memberCount} members</span>
        </div>
        <footer className="home-card__footer home-card__footer--editing">
          <button
            type="button"
            className="home-card__cancel"
            onClick={cancelEdit}
            data-testid={`v2-project-card-cancel-${project.id}`}
          >
            Cancel
          </button>
          <button
            type="button"
            className="home-card__save"
            onClick={saveEdit}
            disabled={!(name || "").trim()}
            data-testid={`v2-project-card-save-${project.id}`}
          >
            Save
          </button>
        </footer>
      </article>
    );
  }

  return (
    <article
      className="home-card home-card--project stone-card"
      data-testid={`v2-project-card-${project.id}`}
    >
      <Link
        className="home-card__link-veil"
        to={`/v2/p/${programId}/${project.id}`}
        aria-label={`Open ${project.name}`}
      />
      <div className="home-card__title" data-testid={`v2-project-card-name-${project.id}`}>
        {project.name}
      </div>
      <div className="home-card__desc" data-testid={`v2-project-card-hyp-${project.id}`}>
        {project.hypothesis || <em className="home-card__desc--placeholder">No hypothesis</em>}
      </div>
      <div className="home-card__meta">
        <span className="home-card__chip home-card__chip--project">Project</span>
        <span className="home-card__count">{project.chatCount} chats</span>
        <span className="home-card__count">{project.memberCount} members</span>
        <span className="home-card__time">{formatRelative(project.lastActiveAt)}</span>
      </div>
      <footer className="home-card__footer">
        <button
          type="button"
          className="home-card__edit"
          onClick={startEdit}
          title="Edit name + hypothesis"
          data-testid={`v2-project-card-edit-${project.id}`}
        >
          ✎ Edit
        </button>
        <button
          type="button"
          className="home-card__delete"
          data-testid={`v2-project-card-delete-${project.id}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (typeof window !== "undefined" && window.confirm(`Delete project "${project.name}"? Its chats remain in your history but the project will be removed.`)) {
              onDelete?.(project.id);
            }
          }}
        >
          ✕ Delete
        </button>
      </footer>
    </article>
  );
};

const ProgramHomeBase = ({ workspaceStore, userStore, historyStore }) => {
  const { program: programId } = useParams();
  const [wizardOpen, setWizardOpen] = useState(false);
  // Wait until Cognito has produced a bearer token. Without this guard,
  // the first useEffect tick can fire before userStore.authData populates,
  // sending requests without an Authorization header and producing
  // 401s in the console even though the user is signed in.
  const authReady = !!userStore?.authData;
  const reloadHistory = useCallback(() => {
    historyStore?.loadHistory?.("CONVERSATION").catch(() => {});
  }, [historyStore]);
  const runningChats = useRunningConversations(historyStore?.conversations,
                                               { auth: userStore?.authData, onEnded: reloadHistory });

  useEffect(() => {
    if (!programId || !authReady) return;
    if (workspaceStore.programs.length === 0) {
      workspaceStore.loadPrograms();
    }
    workspaceStore.loadProjects(programId);
    // On every visit, so a chat left while its agent works shows as running (PLEX-6361).
    reloadHistory();
  }, [programId, workspaceStore, authReady, reloadHistory]);

  const program = workspaceStore.getProgramById(programId);
  const projects = workspaceStore.getProjectsForProgram(programId);
  const projectsLoading = workspaceStore.projectsLoading[programId];
  const projectsError = workspaceStore.projectsError[programId];
  const recentChats = recentV2Conversations(historyStore?.conversations || [], { programId, limit: 8 });
  // A reload keeps the cached list on screen; the skeleton is for a first load only.
  const recentChatsLoading = !!historyStore?.loading && recentChats.length === 0;
  const historyItems = historyStore?.conversations || [];
  const showProjects = !projectsLoading && projects.length > 0;
  const showProjectsEmpty = !projectsLoading && projects.length === 0 && !projectsError;

  if (!programId) return <Navigate to="/v2" replace />;

  return (
    <section className="home stone-page" data-testid="v2-program-home">
      <div className="home__col home__col--main">
        <div className="home__head">
          <h1 className="home__title hero-display">{program?.name || programId}</h1>
          <button
            type="button"
            className="home__teambtn"
            data-testid="v2-program-team-btn"
            disabled
            title="Team drawer arrives in Phase 5"
          >
            👥 Team
          </button>
        </div>
        {program?.description && (
          <p className="home__sub">{program.description}</p>
        )}

        <div className="home__projects-head">
          <h2 className="home__sidetitle">Projects</h2>
          <button
            type="button"
            className="home__newproject"
            onClick={() => setWizardOpen(true)}
            data-testid="v2-program-new-project"
          >
            + New project
          </button>
        </div>
        {projectsLoading && <SkeletonCardGrid count={4} />}
        {projectsError && <div className="home__error" data-testid="v2-program-error">{projectsError}</div>}

        {showProjectsEmpty && (
          <EmptyState
            icon="📂"
            title="No projects yet"
            body="Projects are weeks-to-months R&D investigations. Spin one up to get started."
            action={{ label: "+ New project", onClick: () => setWizardOpen(true), kind: "primary" }}
            testId="v2-program-empty"
          />
        )}

        {showProjects && (
          <div className="home-grid" data-testid="v2-project-grid">
            {projects.map((p) => {
              const liveChatCount = countProjectChats(historyItems, programId, p.id);
              const augmented = { ...p, chatCount: liveChatCount || p.chatCount || 0 };
              return (
                <ProjectCard
                  key={p.id}
                  programId={programId}
                  project={augmented}
                  onRename={(id, name) => workspaceStore.updateProject(programId, id, { name }).catch((e) => console.error(e))}
                  onEditHypothesis={(id, hypothesis) => workspaceStore.updateProject(programId, id, { hypothesis }).catch((e) => console.error(e))}
                  onDelete={(id) => workspaceStore.deleteProject(programId, id).catch((e) => console.error(e))}
                />
              );
            })}
          </div>
        )}
      </div>

      <aside className="home__col home__col--side">
        <h2 className="home__sidetitle">Recent chats</h2>
        {recentChatsLoading
          ? <SkeletonRows count={3} />
          : <RecentChatsList chats={recentChats} runningIds={runningChats}
                             emptyHint="No recent chats in this program." />
        }
      </aside>

      <NewProjectWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        programId={programId}
      />
    </section>
  );
};

const ProgramHome = inject("workspaceStore", "userStore", "historyStore")(observer(ProgramHomeBase));
export default ProgramHome;
